import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiErrorMessage } from '../../shared/api/errors';
import { formatBytes, formatDateTime } from '../../shared/lib/format';
import { Button } from '../../shared/ui/Button';
import { inputClass } from '../../shared/ui/TextField';
import { Card, PageHeader } from '../../shared/ui/primitives';
import { useSession } from '../auth/session';
import {
  deleteSoftware,
  listSoftware,
  SOFTWARE_PLATFORMS,
  softwareQueryKey,
  uploadSoftware,
  type SoftwareDownload,
  type SoftwarePlatform,
} from './api';
import { SoftwareList } from './SoftwareList';

/** «Программы» в кабинете: ссылки на Romexis; администратор загружает
 *  установщики для Windows и macOS. */
export function SoftwarePage() {
  const { user } = useSession();
  const isAdmin = user?.role === 'admin';
  const software = useQuery({ queryKey: softwareQueryKey, queryFn: listSoftware });
  const hasSoftware = (software.data?.length ?? 0) > 0;

  return (
    <section className="space-y-6">
      <PageHeader title="Программа для просмотра снимков" />
      {hasSoftware && (
        <Card className="space-y-3 p-4 sm:p-5">
          <p className="text-sm text-muted">
            Установите Romexis, чтобы открыть скачанные снимки КЛКТ и другие DICOM-файлы.
          </p>
          <SoftwareList />
        </Card>
      )}
      {isAdmin && <AdminUploads />}
    </section>
  );
}

function AdminUploads() {
  const query = useQuery({ queryKey: softwareQueryKey, queryFn: listSoftware });
  const current = new Map((query.data ?? []).map((s) => [s.platform, s]));
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold text-ink">Загрузка установщиков</h2>
      <div className="grid gap-4 md:grid-cols-2">
        {SOFTWARE_PLATFORMS.map((p) => (
          <PlatformUpload
            key={p.id}
            platform={p.id}
            label={p.label}
            accept={p.accept}
            current={current.get(p.id)}
          />
        ))}
      </div>
    </div>
  );
}

function PlatformUpload({
  platform,
  label,
  accept,
  current,
}: {
  platform: SoftwarePlatform;
  label: string;
  accept: string;
  current?: SoftwareDownload;
}) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [version, setVersion] = useState('');
  const [progress, setProgress] = useState(0);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: softwareQueryKey });

  const upload = useMutation({
    mutationFn: () => uploadSoftware(platform, file!, version.trim(), setProgress),
    onMutate: () => setProgress(0),
    onSuccess: () => {
      setFile(null);
      setVersion('');
      if (fileRef.current) fileRef.current.value = '';
      invalidate();
    },
  });
  const remove = useMutation({ mutationFn: () => deleteSoftware(platform), onSuccess: invalidate });

  return (
    <Card className="space-y-3 p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-ink">{label}</h3>
      {current ? (
        <div className="space-y-1 text-xs text-muted">
          <p>
            Сейчас: <span className="text-ink">{current.fileName}</span>
            {current.version && <> · версия {current.version}</>} · {formatBytes(current.size)}
          </p>
          <p>Обновлено {formatDateTime(current.updatedAt)}</p>
        </div>
      ) : (
        <p className="text-xs text-muted">Не загружено.</p>
      )}

      <input
        ref={fileRef}
        type="file"
        accept={accept}
        disabled={upload.isPending}
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        className="block w-full text-sm text-muted file:mr-3 file:rounded-lg file:border-0 file:bg-canvas file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-ink"
      />
      <input
        value={version}
        onChange={(e) => setVersion(e.target.value)}
        placeholder="Версия (необязательно), например 6.4.7"
        maxLength={50}
        disabled={upload.isPending}
        className={inputClass}
      />

      {upload.isPending && (
        <div className="space-y-1">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-canvas">
            <div
              className="h-full bg-accent transition-all"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
          <p className="text-xs text-muted">Загрузка… {Math.round(progress * 100)}%</p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          disabled={!file || upload.isPending}
          onClick={() => upload.mutate()}
        >
          {current ? 'Заменить' : 'Загрузить'}
        </Button>
        {current && (
          <Button
            type="button"
            size="sm"
            variant="danger"
            disabled={remove.isPending || upload.isPending}
            onClick={() => {
              if (
                window.confirm(
                  `Удалить программу для ${label}? Ссылки в письмах перестанут работать.`,
                )
              ) {
                remove.mutate();
              }
            }}
          >
            Удалить
          </Button>
        )}
      </div>
      {upload.isError && (
        <p className="text-sm text-danger">
          {apiErrorMessage(upload.error, 'Не удалось загрузить файл')}
        </p>
      )}
      {remove.isError && (
        <p className="text-sm text-danger">{apiErrorMessage(remove.error, 'Не удалось удалить')}</p>
      )}
    </Card>
  );
}
